// File: src/pages/NewsTickerAdmin.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import cfgDefault, { API_BASE_URL as API_NAMED } from "../config/config";
import AdminPageFrame from "../components/admin/AdminPageFrame";

/** Defaults identisch zum Backend */
const defaultSettings = {
  position: "fixed",
  placement: "top",
  width: "100%",
  background_color: "#facc15",
  text_color: "#000000",
  border: "",
  padding: "10px",
  margin: "0",
  animation_type: "marquee",
  animation_speed: 40,
  pause_on_hover: false,
  autoplay: true,
  show_close_button: true,
  height_px: 42,
};

/** API-Basis robust auflösen (named + default Export + ENV + Heuristik) */
function resolveApiBase() {
  // WICHTIG: 'process' existiert im Browser nicht. Daher erst prüfen:
  const fromEnv = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL) || undefined;

  const candidates = [
    API_NAMED,
    cfgDefault?.API_BASE_URL,
    cfgDefault?.apiBaseUrl,
    cfgDefault?.API_BASE_URL_IONOS,
    fromEnv,
    (typeof window !== "undefined" && window.__E4G_API_BASE_URL__),
    (typeof window !== "undefined" && window.location?.origin?.replace("www.", "api.")),
  ].filter(Boolean);

  for (const u of candidates) {
    try { return new URL(u).origin; } catch {}
  }
  return "https://api.experts4europe.com";
}

/** GET ohne Custom-Header (verhindert CORS-Preflight), mit Cache-Buster */
function noStoreFetchGET(url) {
  const sep = url.includes("?") ? "&" : "?";
  return fetch(`${url}${sep}_=${Date.now()}`, { cache: "no-store", mode: "cors", credentials: "omit" });
}

export default function NewsTickerAdmin() {
  const apiBase = resolveApiBase();

  const [settings, setSettings]   = useState(defaultSettings);
  const [messages, setMessages]   = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [newLink, setNewLink]       = useState("");
  const [saving, setSaving]       = useState(false);
  const trackRef = useRef(null);

  // Laden (GET -> ohne Preflight)
  useEffect(() => {
    (async () => {
      try {
        const s = await noStoreFetchGET(`${apiBase}/api/newsticker/settings`);
        if (!s.ok) throw new Error(`GET /settings → ${s.status} ${s.statusText} (api=${apiBase})`);
        const data = await s.json();
        setSettings({ ...defaultSettings, ...data });
      } catch (e) {
        console.error("Settings laden fehlgeschlagen:", e);
        alert(`Fehler beim Laden der Einstellungen: ${e.message}`);
      }
      try {
        const r = await noStoreFetchGET(`${apiBase}/api/newsticker/messages`);
        if (!r.ok) throw new Error(`GET /messages → ${r.status} ${r.statusText} (api=${apiBase})`);
        const data = await r.json();
        setMessages(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Nachrichten laden fehlgeschlagen:", err);
        alert(`Fehler beim Laden der Nachrichten: ${err.message}`);
      }
    })();
  }, [apiBase]);

  // Speichern Settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${apiBase}/api/newsticker/settings`, {
        method: "POST", mode: "cors", credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!r.ok) throw new Error(`POST /settings → ${r.status} ${r.statusText} (api=${apiBase})`);
      const data = await r.json();
      setSettings((prev) => ({ ...prev, ...data }));
    } catch (e) {
      console.error("POST /settings failed", e);
      alert(`Fehler beim Speichern: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Nachrichten – CRUD
  const handleAddMessage = async () => {
    const msg = newMessage.trim();
    if (!msg) return;
    try {
      const payload = { message: msg, link: newLink.trim() || "", display_order: (messages?.length || 0) + 1 };
      const r = await fetch(`${apiBase}/api/newsticker/messages`, {
        method: "POST", mode: "cors", credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`POST /messages → ${r.status} ${r.statusText} (api=${apiBase})`);
      const created = await r.json();
      setMessages([...(messages || []), created]);
      setNewMessage("");
      setNewLink("");
    } catch (e) {
      console.error("POST /messages failed", e);
      alert(`Fehler beim Hinzufügen: ${e.message}`);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      const r = await fetch(`${apiBase}/api/newsticker/messages/${id}`, {
        method: "DELETE", mode: "cors", credentials: "omit"
      });
      if (!r.ok) throw new Error(`DELETE /messages/${id} → ${r.status} ${r.statusText} (api=${apiBase})`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      console.error("DELETE /messages failed", e);
      alert(`Fehler beim Löschen: ${e.message}`);
    }
  };

  const handleUpdateMessage = async (id, patch) => {
    try {
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
      const r = await fetch(`${apiBase}/api/newsticker/messages/${id}`, {
        method: "PUT", mode: "cors", credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`PUT /messages/${id} → ${r.status} ${r.statusText} (api=${apiBase})`);
      const updated = await r.json();
      setMessages((prev) => prev.map((m) => (m.id === id ? updated : m)));
    } catch (e) {
      console.error("PUT /messages failed", e);
      alert(`Fehler beim Speichern: ${e.message}`);
    }
  };

  // Drag & Drop Reorder
  const dragIndex = useRef(-1);
  const onDragStart = (i) => () => (dragIndex.current = i);
  const onDragOver  = (e) => e.preventDefault();
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    const from = dragIndex.current, to = i; dragIndex.current = -1;
    if (from === -1 || from === to) return;

    const next = [...messages]; const [moved] = next.splice(from, 1); next.splice(to, 0, moved);
    const re    = next.map((m, j) => ({ ...m, display_order: j + 1 }));
    setMessages(re);

    try {
      const ids = re.map((m) => m.id);
      const r = await fetch(`${apiBase}/api/newsticker/messages/reorder`, {
        method: "POST", mode: "cors", credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: ids }),
      });
      if (!r.ok) throw new Error(`POST /reorder → ${r.status} ${r.statusText} (api=${apiBase})`);
      const serverRows = await r.json();
      setMessages(Array.isArray(serverRows) ? serverRows : re);
    } catch (e2) {
      console.error("POST /reorder failed", e2);
      alert(`Fehler beim Speichern der Reihenfolge: ${e2.message}`);
    }
  };

  // Form-Handler
  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // Vorschau-Animation
  const previewAnimation = useMemo(() => {
    if (!settings.autoplay) return "none";
    const s = Math.max(1, Number(settings.animation_speed) || 40);
    switch (settings.animation_type) {
      case "marquee": return `ticker-loop ${s}s linear infinite`;
      case "slide":   return `ticker-slide ${s}s ease-in-out infinite`;
      case "fade":    return `ticker-fade ${s}s ease-in-out infinite`;
      case "typing":  return `ticker-typing ${Math.max(2, s / 2)}s steps(30, end) infinite`;
      default:        return "none";
    }
  }, [settings.animation_type, settings.animation_speed, settings.autoplay]);

  const sortedMessages = useMemo(
    () => [...(messages || [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [messages]
  );

  const onMouseEnter = () => {
    if (!settings.pause_on_hover || !trackRef.current) return;
    trackRef.current.style.animationPlayState = "paused";
  };
  const onMouseLeave = () => {
    if (!trackRef.current) return;
    trackRef.current.style.animationPlayState = "running";
  };

  return (
    <AdminPageFrame
      title="NewsTicker Administration"
      subtitle="Ticker-Einstellungen, Nachrichtenreihenfolge und Live-Vorschau in derselben Admin-Shell."
      badge="Broadcast Operations"
      contentClassName="space-y-6"
    >

      {/* Einstellungen */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6 space-y-6">
        <h2 className="text-xl font-semibold">Einstellungen</h2>

        {/* Position & Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200">Position</label>
            <select name="position" value={settings.position} onChange={handleSettingsChange}
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3">
              <option>fixed</option><option>absolute</option><option>relative</option><option>sticky</option>
            </select>
            <p className="text-xs text-slate-400 mt-1">„fixed“ ist für einen globalen Ticker am sichersten.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Platzierung</label>
            <select name="placement" value={settings.placement} onChange={handleSettingsChange}
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3">
              <option value="top">Oben</option><option value="bottom">Unten</option>
              <option value="left">Links</option><option value="right">Rechts</option>
            </select>
            <p className="text-xs text-slate-400 mt-1">Meistens „Oben“.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Breite</label>
            <input name="width" value={settings.width} onChange={handleSettingsChange} placeholder="100% / 1200px"
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3" />
            <p className="text-xs text-slate-400 mt-1">z. B. 100% oder 1200px.</p>
          </div>
        </div>

        {/* Design */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200">Hintergrund</label>
            <input type="color" name="background_color" value={settings.background_color}
              onChange={handleSettingsChange} className="mt-1 h-10 w-full rounded-xl border border-slate-700 bg-slate-900 px-2" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Textfarbe</label>
            <input type="color" name="text_color" value={settings.text_color}
              onChange={handleSettingsChange} className="mt-1 h-10 w-full rounded-xl border border-slate-700 bg-slate-900 px-2" />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-slate-200">Border (CSS)</label>
            <input name="border" value={settings.border} onChange={handleSettingsChange} placeholder="1px solid #000"
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3" />
            <p className="text-xs text-slate-400 mt-1">Optional, z. B. „1px solid #000“.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Padding</label>
            <input name="padding" value={settings.padding} onChange={handleSettingsChange} placeholder="10px"
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Margin</label>
            <input name="margin" value={settings.margin} onChange={handleSettingsChange} placeholder="0"
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Höhe (px)</label>
            <input type="number" min={28} name="height_px" value={settings.height_px} onChange={handleSettingsChange}
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3" />
          </div>
        </div>

        {/* Animation & Verhalten */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200">Animation</label>
            <select name="animation_type" value={settings.animation_type} onChange={handleSettingsChange}
              className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3">
              <option value="marquee">Marquee</option>
              <option value="slide">Slide</option>
              <option value="fade">Fade</option>
              <option value="typing">Typing</option>
              <option value="none">Keine</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200">Geschwindigkeit (s)</label>
            <input type="number" min={1} name="animation_speed" value={settings.animation_speed}
              onChange={handleSettingsChange} className="mt-1 w-full h-10 rounded-xl border border-slate-700 bg-slate-900 px-3" />
            <p className="text-xs text-slate-400 mt-1">Je größer, desto langsamer.</p>
          </div>

          <label className="flex items-center gap-3">
            <input type="checkbox" name="pause_on_hover" checked={!!settings.pause_on_hover}
              onChange={handleSettingsChange} className="h-5 w-5" />
            <span className="text-sm font-medium text-slate-200">Pause bei Hover</span>
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <label className="flex items-center gap-3">
            <input type="checkbox" name="autoplay" checked={!!settings.autoplay}
              onChange={handleSettingsChange} className="h-5 w-5" />
            <span className="text-sm font-medium text-slate-200">Autoplay</span>
          </label>

          <label className="flex items-center gap-3">
            <input type="checkbox" name="show_close_button" checked={!!settings.show_close_button}
              onChange={handleSettingsChange} className="h-5 w-5" />
            <span className="text-sm font-medium text-slate-200">Schließen‑Button</span>
          </label>
        </div>

        <div className="flex gap-3">
          <button onClick={handleSaveSettings}
            className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:brightness-110"
            disabled={saving}>{saving ? "Speichere…" : "Einstellungen speichern"}</button>
          <button onClick={() => setSettings(defaultSettings)} className="px-4 py-2 rounded-xl border border-slate-600 hover:bg-slate-800" type="button">
            Standard wiederherstellen
          </button>
        </div>
      </section>

      {/* Nachrichten */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6 mt-6">
        <h2 className="text-xl font-semibold mb-4">Nachrichten</h2>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-[1fr_minmax(200px,1fr)_auto] gap-2">
          <input className="h-10 rounded-xl border border-slate-700 bg-slate-900 px-3"
                 value={newMessage} onChange={(e)=>setNewMessage(e.target.value)} placeholder="Neue Nachricht hinzufügen" />
          <input className="h-10 rounded-xl border border-slate-700 bg-slate-900 px-3"
                 value={newLink} onChange={(e)=>setNewLink(e.target.value)} placeholder="Optionaler Link (https://…)" />
          <button onClick={handleAddMessage} className="h-10 px-4 rounded-xl bg-emerald-600 text-white hover:brightness-110">+ Hinzufügen</button>
        </div>

        <ul className="space-y-2">
          {[...sortedMessages].map((msg, index) => (
            <li key={msg.id} draggable onDragStart={onDragStart(index)} onDragOver={onDragOver} onDrop={onDrop(index)}
                className="flex flex-col md:flex-row md:items-center gap-2 rounded-xl border border-slate-700 p-2 bg-slate-950"
                title="Ziehen zum Sortieren">
              <div className="cursor-grab select-none md:w-10 text-center py-2" aria-hidden>≡</div>
              <input className="h-10 flex-1 rounded-xl border border-slate-700 bg-white px-3 text-black"
                     value={msg.message ?? ""} onChange={(e)=>handleUpdateMessage(msg.id, { message: e.target.value })} placeholder="Nachrichtentext" />
              <input className="h-10 flex-1 rounded-xl border border-slate-700 bg-white px-3 text-black"
                     value={msg.link ?? ""} onChange={(e)=>handleUpdateMessage(msg.id, { link: e.target.value })} placeholder="https://…" />
              <div className="w-14 text-center text-sm opacity-60">#{msg.display_order}</div>
              <button onClick={()=>handleDeleteMessage(msg.id)} className="h-10 px-3 rounded-xl border border-red-600 text-red-400 hover:bg-red-600 hover:text-white">Löschen</button>
            </li>
          ))}
        </ul>
      </section>

      {/* Vorschau */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6 mt-6">
        <h2 className="text-xl font-semibold mb-3">Vorschau</h2>

        <div className="ticker-container bg-slate-900 text-slate-50 relative"
             style={{
               position: settings.position, [settings.placement]: 0, width: settings.width,
               backgroundColor: settings.background_color || undefined, border: settings.border || undefined,
               padding: settings.padding, margin: settings.margin, height: `${settings.height_px || 42}px`,
               overflow: "hidden", whiteSpace: "nowrap", zIndex: 1200,
             }}
             onMouseEnter={()=>{ if(settings.pause_on_hover && trackRef.current) trackRef.current.style.animationPlayState='paused'; }}
             onMouseLeave={()=>{ if(trackRef.current) trackRef.current.style.animationPlayState='running'; }}>
          <div ref={trackRef} className="ticker-track" style={{ animation: previewAnimation }}>
            {sortedMessages.map((m, i) => (
              <span key={m.id ?? i} className="ticker-message" style={{ marginRight: "3rem", color: settings.text_color }}>
                {m.link ? <a href={m.link} target="_blank" rel="noreferrer" className="underline ticker-text">{m.message || "(leer)"}</a>
                        : <span className="ticker-text">{m.message || "(leer)"}</span>}
              </span>
            ))}
            {["marquee","slide"].includes(settings.animation_type) && sortedMessages.map((m, i) => (
              <span key={`dup-${m.id ?? i}`} className="ticker-message" style={{ marginRight:"3rem", color: settings.text_color }} aria-hidden="true">
                {m.link ? <a href={m.link} target="_blank" rel="noreferrer" className="underline ticker-text">{m.message || "(leer)"}</a>
                        : <span className="ticker-text">{m.message || "(leer)"}</span>}
              </span>
            ))}
          </div>

          {settings.show_close_button && (
            <button style={{ position:"absolute", right:6, top:6, background:"transparent", border:"none", fontSize:"1rem",
                             cursor:"pointer", color: settings.text_color || "inherit" }}
                    onClick={()=> alert("Ticker wird in der Live-Ansicht geschlossen")} aria-label="Ticker schließen" title="Ticker schließen">×</button>
          )}
        </div>

        <style>{`
          @keyframes ticker-loop { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
          @keyframes ticker-slide { 0% { transform: translateX(0); } 50% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
          @keyframes ticker-fade  { 0%,100% { opacity: .25; } 50% { opacity: 1; } }
          @keyframes ticker-typing{ 0% { clip-path: inset(0 100% 0 0); } 50% { clip-path: inset(0 0 0 0); } 100% { clip-path: inset(0 100% 0 0); } }
        `}</style>
      </section>
    </AdminPageFrame>
  );
}
